import { Building2, Check, ChevronLeft, ChevronRight, Crown, Image as ImageIcon, Upload } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";

import { useCompanyDashboard } from "./CompanyDashboardProvider";
import { getCompanyDashboardPath } from "./paths";
import "./styles.css";

const SAMPLE_JOBS = [
	{ title: "Senior Software Engineer", location: "Dar es Salaam", type: "Full-time" },
	{ title: "Product Manager", location: "Dar es Salaam", type: "Full-time" },
	{ title: "Recruiter", location: "Remote", type: "Contract" },
	{ title: "Operations Lead", location: "Arusha", type: "Full-time" },
];

export default function SetupPage() {
	const navigate = useNavigate();
	const { companyId, companyName, branding, updateBranding } = useCompanyDashboard();
	const [step, setStep] = React.useState(1);
	const [selectedJobs, setSelectedJobs] = React.useState([0, 1, 2]);
	const logoRef = React.useRef(null);
	const bannerRef = React.useRef(null);
	const isEnterprise = branding.plan === "enterprise";
	const maxSteps = isEnterprise ? 5 : 4;

	const setFile = (event, field, extraUpdates = {}) => {
		const file = event.target.files?.[0];
		if (!file) return;
		const reader = new FileReader();
		reader.onload = () => updateBranding({ [field]: reader.result, ...extraUpdates });
		reader.readAsDataURL(file);
		event.target.value = "";
	};

	const next = () => {
		if (step === 2 && !isEnterprise) {
			setStep(3);
			return;
		}

		if (step >= maxSteps) {
			navigate(getCompanyDashboardPath("home"));
			return;
		}

		setStep((current) => current + 1);
	};

	const previous = () => {
		if (step === 3 && !isEnterprise) {
			setStep(1);
			return;
		}
		setStep((current) => Math.max(1, current - 1));
	};

	return (
		<div className="company-dashboard-app flex min-h-screen items-center justify-center bg-slate-50 p-6">
			<div className="w-full max-w-3xl overflow-hidden rounded-3xl border border-border bg-white shadow-xl">
				<div className="border-b border-border p-6">
					<div className="mb-4 flex items-center justify-between gap-3">
						<div className="flex items-center gap-3">
							<div className="company-dashboard-gradient flex h-12 w-12 items-center justify-center rounded-2xl">
								<Building2 className="h-6 w-6 text-white" />
							</div>
							<div>
								<h1 className="text-lg font-bold text-foreground">Set Up Your Company Dashboard</h1>
								<p className="text-sm text-muted-foreground">{companyName}</p>
							</div>
						</div>
						<button
							type="button"
							className="text-sm text-muted-foreground hover:text-foreground"
							onClick={() => navigate(getCompanyDashboardPath("home"))}
						>
							Skip for now
						</button>
					</div>
					<div className="h-2 overflow-hidden rounded-full bg-secondary">
						<div
							className="company-dashboard-gradient h-full rounded-full transition-all duration-300"
							style={{ width: `${((step - 1) / (maxSteps - 1)) * 100}%` }}
						/>
					</div>
					<div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
						<span>
							Step {step} of {maxSteps}
						</span>
						<span>{Math.round(((step - 1) / (maxSteps - 1)) * 100)}% complete</span>
					</div>
				</div>

				<div className="p-8">
					{step === 1 ? (
						<div className="text-center">
							<h2 className="text-2xl font-bold text-foreground">Upload Your Logo</h2>
							<p className="mt-2 text-sm text-muted-foreground">
								Your logo anchors the rest of the company dashboard preview.
							</p>
							<div className="my-8">
								<div
									className={`mx-auto flex h-36 w-36 items-center justify-center overflow-hidden rounded-3xl ${branding.companyLogo ? "bg-white shadow-sm" : "border-2 border-dashed border-border bg-secondary"}`}
								>
									{branding.companyLogo ? (
										<img
											src={branding.companyLogo}
											alt={`${companyName} logo`}
											className="h-full w-full object-cover"
										/>
									) : (
										<ImageIcon className="h-12 w-12 text-muted-foreground" />
									)}
								</div>
							</div>
							<input
								ref={logoRef}
								type="file"
								accept="image/*"
								className="hidden"
								onChange={(event) => setFile(event, "companyLogo")}
							/>
							<Button onClick={() => logoRef.current?.click()}>
								<Upload className="mr-2 h-4 w-4" />
								{branding.companyLogo ? "Change Logo" : "Upload Logo"}
							</Button>
						</div>
					) : null}

					{step === 2 ? (
						<div className="text-center">
							<h2 className="text-2xl font-bold text-foreground">Banner Image</h2>
							<p className="mt-2 text-sm text-muted-foreground">
								Use a branded banner for a stronger employer presentation.
							</p>
							{isEnterprise ? (
								<>
									<div
										className={`my-8 flex h-48 w-full items-center justify-center overflow-hidden rounded-3xl ${branding.companyBanner ? "bg-white shadow-sm" : "border-2 border-dashed border-border bg-secondary"}`}
									>
										{branding.companyBanner ? (
											<img
												src={branding.companyBanner}
												alt={`${companyName} banner`}
												className="h-full w-full object-cover"
											/>
										) : (
											<ImageIcon className="h-12 w-12 text-muted-foreground" />
										)}
									</div>
									<input
										ref={bannerRef}
										type="file"
										accept="image/*"
										className="hidden"
										onChange={(event) => setFile(event, "companyBanner", { bannerStyle: "custom" })}
									/>
									<Button onClick={() => bannerRef.current?.click()}>
										<Upload className="mr-2 h-4 w-4" />
										{branding.companyBanner ? "Change Banner" : "Upload Banner"}
									</Button>
								</>
							) : (
								<div className="my-8 rounded-3xl border border-amber-100 bg-amber-50 p-8">
									<Crown className="mx-auto h-10 w-10 text-amber-500" />
									<p className="mt-3 text-sm text-muted-foreground">
										Custom banner uploads are reserved for Enterprise.
									</p>
								</div>
							)}
						</div>
					) : null}

					{step === 3 ? (
						<div>
							<h2 className="text-center text-2xl font-bold text-foreground">Tell Candidates About You</h2>
							<p className="mt-2 text-center text-sm text-muted-foreground">
								Write the company summary that should appear in the dashboard preview.
							</p>
							<textarea
								rows={7}
								value={branding.about}
								onChange={(event) => updateBranding({ about: event.target.value })}
								className="mt-8 w-full rounded-3xl border border-input px-4 py-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
								placeholder="Describe what the company does, the mission, and what candidates can expect."
							/>
						</div>
					) : null}

					{step === 4 ? (
						<div>
							<h2 className="text-center text-2xl font-bold text-foreground">Review Company Jobs</h2>
							<p className="mt-2 text-center text-sm text-muted-foreground">
								Choose which roles you want top-of-mind while finishing setup.
							</p>
							<div className="mt-8 space-y-3">
								{SAMPLE_JOBS.map((job, index) => {
									const selected = selectedJobs.includes(index);
									return (
										<button
											key={job.title}
											type="button"
											onClick={() =>
												setSelectedJobs((current) =>
													selected ? current.filter((item) => item !== index) : [...current, index],
												)
											}
											className={`flex w-full items-center justify-between rounded-2xl border p-4 text-left transition-colors ${selected ? "border-primary-300 bg-blue-50" : "border-border bg-white hover:bg-secondary/50"}`}
										>
											<div>
												<p className="text-sm font-semibold text-foreground">{job.title}</p>
												<p className="text-sm text-muted-foreground">
													{job.location} • {job.type}
												</p>
											</div>
											<div
												className={`flex h-7 w-7 items-center justify-center rounded-full ${selected ? "bg-primary-700 text-white" : "border border-border text-muted-foreground"}`}
											>
												{selected ? <Check className="h-4 w-4" /> : null}
											</div>
										</button>
									);
								})}
							</div>
						</div>
					) : null}

					{step === 5 && isEnterprise ? (
						<div className="text-center">
							<h2 className="text-2xl font-bold text-foreground">Activate Your Premium Employer Presence</h2>
							<p className="mt-2 text-sm text-muted-foreground">
								Enterprise unlocks custom presentation layers for heavy hiring teams.
							</p>
							<div className="mt-8 rounded-3xl border border-primary-100 bg-blue-50 p-8">
								<p className="text-sm text-muted-foreground">Company dashboard preview URL</p>
								<p className="mt-2 text-xl font-bold text-primary-700">
									{window.location.origin}/companies/{companyId}
								</p>
							</div>
						</div>
					) : null}
				</div>

				<div className="flex items-center justify-between border-t border-border p-6">
					<Button variant="outline" onClick={previous} disabled={step === 1}>
						<ChevronLeft className="mr-2 h-4 w-4" />
						Back
					</Button>
					<Button onClick={next}>
						{step === maxSteps ? "Finish Setup" : "Continue"}
						<ChevronRight className="ml-2 h-4 w-4" />
					</Button>
				</div>
			</div>
		</div>
	);
}
